import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { COLORS, FONTS, GRADIENTS, media } from '../../../components/ui/theme';
import LOGO_ICON from '../../../assets/images/Logo_EIV_Gradient-Icone.png';

/* ════════════════════════════════════════════════════════════════════════════
 * FOOTER — version compacte DA V2 (1:1 preview dav2-fusion-B)
 * Fond sombre inkDeep, liseré dégradé accent en haut, TOUT le texte blanc.
 * Logo blanc + icônes sociales SVG inline + une ligne légale.
 * ════════════════════════════════════════════════════════════════════════════ */

const FooterRoot = styled.footer`
  background: ${COLORS.inkDeep};
  color: #fff;
  padding: 0.95rem 1.6rem 0.85rem;
  text-align: center;
  font-size: 0.82rem;
  font-family: ${FONTS.body};
  border-top: 0.3rem solid transparent;
  border-image: ${GRADIENTS.accent} 1;

  ${media.sm`
    padding: 0.85rem 1.1rem 0.75rem;
  `}
`;

const Logo = styled.div`
  img {
    /* icône EV (carrée) — légèrement plus grande que le wordmark horizontal pour la présence */
    height: 3.4rem;
    width: auto;
    margin: 0 auto 0.55rem;
    display: block;
    /* logo BLANC sur fond noir : rend l'icône EV (n'importe quelle couleur source) en blanc pur
       en préservant l'alpha (brightness(0)=noir + invert=blanc). Demande Jonathan 2026-06-11. */
    filter: brightness(0) invert(1);
  }
`;

const Socials = styled.div`
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.55rem;

  a {
    display: inline-flex;
    color: #fff;
    cursor: pointer;
    transition: color 0.15s;
  }

  a:hover {
    color: ${COLORS.accentBright};
  }

  svg {
    width: 1.3rem;
    height: 1.3rem;
    display: block;
    fill: currentColor;
  }
`;

const Legal = styled.div`
  color: #fff;
  font-weight: 400;

  a {
    color: #fff;
  }

  a:hover {
    color: ${COLORS.accentBright};
  }

  span {
    color: #fff;
  }
`;

/* Réseaux sociaux — URLs conservées 1:1 depuis l'ancien footer (window.open). */
const SOCIALS = [
  {
    label: 'X (Twitter)',
    url: 'https://twitter.com/Earniverse_io',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z',
  },
  {
    label: 'Facebook',
    url: 'https://www.facebook.com/earniverseofficial',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z',
  },
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/earniverse_official/',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311 1.266-.058 1.646-.07 4.85-.07Zm0 1.802c-3.149 0-3.523.012-4.767.069-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.057 1.244-.069 1.618-.069 4.767s.012 3.523.069 4.767c.045.975.207 1.504.344 1.857.182.467.398.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.244.057 1.618.069 4.767.069s3.523-.012 4.767-.069c.975-.045 1.504-.207 1.857-.344.467-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.057-1.244.069-1.618.069-4.767s-.012-3.523-.069-4.767c-.045-.975-.207-1.504-.344-1.857-.182-.467-.398-.8-.748-1.15-.35-.35-.683-.566-1.15-.748-.353-.137-.882-.3-1.857-.344-1.244-.057-1.618-.069-4.767-.069Zm0 3.064a6.135 6.135 0 1 0 0 12.27 6.135 6.135 0 0 0 0-12.27Zm0 10.122a3.987 3.987 0 1 1 0-7.974 3.987 3.987 0 0 1 0 7.974Zm6.406-10.845a1.434 1.434 0 1 1-2.868 0 1.434 1.434 0 0 1 2.868 0Z',
  },
  {
    label: 'Telegram',
    url: 'https://t.me/earniverse',
    path: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0Zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635Z',
  },
  {
    label: 'YouTube',
    url: 'https://www.youtube.com/@Earniverse',
    path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z',
  },
];

function Footer() {
  const { t } = useTranslation();
  return (
    <FooterRoot>
      <Logo>
        <img src={LOGO_ICON} loading="lazy" alt="Earniverse" />
      </Logo>
      <Socials>
        {SOCIALS.map((s) => (
          <a
            key={s.label}
            aria-label={s.label}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
              <path d={s.path} />
            </svg>
          </a>
        ))}
      </Socials>
      <Legal>
        <a href="https://earniverse.io/privacy-policy/" target="_blank" rel="noreferrer">
          {t("PrivacyPolicy")}
        </a>{' '}
        ·{' '}
        <a href="http://earniverse.io/terms-of-service/" target="_blank" rel="noreferrer">
          {t("TermsOfServices")}
        </a>{' '}
        · &copy; {new Date().getFullYear()} <span>Earniverse.io</span> &mdash; {t("AllRightsReserved")}
      </Legal>
    </FooterRoot>
  );
}

export default Footer;
